<template>
  <div class="reuseAdCard">
    <div v-if="isLoading" class="d-flex justify-content-center mt-4">
      <hl-Loader />
    </div>

    <template v-else-if="ad">
      <template v-if="ad.type === 'propCloud'">
        <div class="d-flex align-items-center">
          <i class="budicon-alert-sign text-warning font-weight-bold h4" />
          <h4 class="ml-2">Er dette din annonse?</h4>
        </div>

        <p class="text-gray2 mt-1">
          Denne annonsen samsvarer med informasjonen du har oppgitt om leieobjektet. Vær
          oppmerksom på at du kun kan bruke annonsen dersom du er eieren av innholdet.
        </p>

        <hr />
      </template>

      <div class="reuseAdCard__content" :class="{'mt-4': ad.type === 'propCloud'}">
        <img
          v-if="ad.imageUrl"
          :src="((env === 'production' || ad.imageUrl.match('https://')) ? '' : 'https://test.no.0x484c.com') + ad.imageUrl"
          class="reuseAdCard__content__adImage"
        />
        <img
          v-else
          src="@modules/husleie-framework/img/graphic-elements/maps/map_marker.png"
          class="reuseAdCard__content__adImage"
        />

        <div>
          <a v-if="ad.href" :href="ad.href" target="_blank" class="text-body" rel="noopener">
            <h5 class="reuseAdCard__content__header">
              {{ ad.header }}
            </h5>
          </a>

          <h5 v-else class="reuseAdCard__content__header">
            {{ ad.header }}
          </h5>

          <template v-if="ad.type === 'letting'">
            <p class="text-gray2">
              {{ ad.street }}, {{ ad.postalZipCode }} {{ ad.postalArea }}
            </p>

            <hl-Button
              :href="pubomaticAppCreateAdLink"
              target="_blank"
              rel="noopener"
              class="d-inline-flex align-items-center"
            >
              <i class="budicon-stack-folders font-weight-bold" />
              <span class="ml-2 font-weight-bold">Gjenbruk denne annonsen</span>
            </hl-Button>
          </template>

          <template v-else-if="ad.type === 'propCloud'">
            <p class="text-gray2">
              {{ ad.address }}
            </p>

            <hl-Button
              :href="pubomaticAppCreateAdLink"
              target="_blank"
              rel="noopener"
              class="d-inline-flex align-items-center"
            >
              <i class="budicon-files-upload font-weight-bold" />
              <span class="ml-2 font-weight-bold">Innhent annonse</span>
            </hl-Button>

            <div class="d-flex align-items-center mt-3">
              <span class="text-gray2">
                Hentet fra
              </span>

              <img src="@/assets/finn.png" class="reuseAdCard__content__finnImage ml-2" />
            
              <Tooltip>
                <template v-slot:trigger>
                  <div>
                    <i class="ml-2 budicon-alert-sign font-weight-bold h5 mt-1 text-gray2" />
                  </div>
                </template>

                <p>
                  I samarbeid med Placepoint henter vi inn tidligere annonser som har vært publisert
                  på FINN. Vær oppmerksom på at du kun kan bruke annonsen dersom du er eier
                  av innholdet.
                </p>
              </Tooltip>
            </div>
          </template>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="d-flex justify-content-between">
        <h5>
          Ingen annonse? Hent en tidligere annonse fra FINN
        </h5>

        <hl-Tag variant="primary" isLargeBorderRadius class="ml-2 h-100">
          Nyhet
        </hl-Tag>
      </div>

      <p class="text-gray2">
        Husk at du bare kan bruke annonsen hvis du eier innholdet.
      </p>

      <hl-Button
        :href="`/dashboard/landlord/${landlordId}/rentalObject/${rentalObjectId}/enrich?origin=create_letting`"
        target="_blank"
        rel="noopener"
        class="d-inline-flex align-items-center"
      >
        <i class="budicon-cloud-download font-weight-bold" />
        <span class="ml-2 font-weight-bold">Hent annonse fra FINN</span>
      </hl-Button>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import Tag from '@modules/husleie-framework/src/components/forms/inputs/tags/Tag.vue';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import Tooltip from '@/components/Tooltip.vue';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';

export default Vue.extend({
  name: 'ReuseAdCard',
  data() {
    return {
      env: process.env.NODE_ENV,
    };
  },
  props: {
    landlordId: {
      type: Number,
      required: true,
    },
    rentalObjectId: {
      type: Number,
      required: true,
    },
    ad: {
      type: Object,
      required: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    'hl-Tag': Tag,
    'hl-Loader': Loader,
    Tooltip,
  },
  computed: {
    pubomaticAppCreateAdLink() {
      return RedirectOperations.getURL(AppRedirect.AD_CREATE, {
        landlordId: this.landlordId,
        rentalObjectId: this.rentalObjectId,
        lettingId: this.ad.lettingId,
        propCloudLettingId: this.ad.propCloudLettingId,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.reuseAdCard {
  border: 1px solid #e9eaeb;
  border-radius: 12px;
  padding: 1.5rem;
}
.reuseAdCard__content__adImage {
  width: 100%;
  height: 169px;
  object-fit: cover;
  border-radius: 8px;
}
.reuseAdCard__content__header {
  margin-top: 1rem;
}
.reuseAdCard__content__finnImage {
  width: 70px;
}

@media (min-width: 650px) {
  .reuseAdCard__content {
    display: flex;
    gap: 0 1.5rem;
  }
  .reuseAdCard__content__adImage {
    width: 220px;
  }
  .reuseAdCard__content__header {
    margin-top: 0;
  }
}
</style>
